import { ContactFormData } from '@upright/common/activities/contact-forms/facade/contact-form';
import { RequestStatus } from '@upright/ui/api/common/types';
import { Button } from '@upright/ui/common/generic/components/button';
import { usePushAnonymousGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { Input, TextArea } from '@upright/ui/common/generic/components/input';
import { Box, Stack } from '@upright/ui/common/generic/components/layout';
import { Heading, Paragraph, Text } from '@upright/ui/common/generic/components/typography';
import { useEffect, useState } from 'react';
import { SuccessCheckmark } from '../../generic/components/success-check-mark';
import { BouColor } from '../../generic/global/bou-colors';

interface Props {
  fields: Partial<ContactFormData>;
  setFields: (newFields: Partial<ContactFormData>) => void;
  validators: {
    [key in keyof ContactFormData]: (value: ContactFormData[key] | undefined) => boolean;
  };
  submit: () => void;
  postStatus: RequestStatus;
  error?: string;
  canSubmit: boolean;
  variant?: ContactFormVariant;
}

export type ContactFormVariant = 'contact' | 'feedback';

/**
 * The same form serves two asks: the Contact entries across the product and the facade, and the
 * unified shell's Feedback button (PLT-6962). Only the words differ; the fields and the
 * submission path are shared.
 */
const CONTACT_FORM_COPY = {
  contact: {
    title: 'Contact us',
    lede: 'Interested in purchasing our products, or want to give feedback? Send the Upright team a message. Only the message is required.',
    submit: 'Send message',
    sentTitle: 'Message sent',
    sentBody: 'Thanks for reaching out.',
  },
  feedback: {
    title: 'Give feedback',
    lede: 'What works well, and what could we improve? Share your thoughts with the Upright team. Only the message is required.',
    submit: 'Send feedback',
    sentTitle: 'Feedback sent',
    sentBody: 'Thanks for helping us improve Upright.',
  },
} as const;

/**
 * Contact form for "non-lead-forms", i.e. for forms that are about providing feedback, or
 * requesting support, but that are not sales-relevant. (See LeadForm.)
 *
 * The chrome is the caller's: `ContactFormModal` wraps this in the design system's `Modal`,
 * which paints the white card, the padding and the close button, so there is no title bar or
 * footer band here. What this renders is the title, the ask, the fields and the action row.
 *
 * **Four regions at `2xl`, and everything inside one of them at `xl`**: the title to its lede,
 * the contact rows to each other, and then each field's own label and error at the `md` and
 * `sm` `Field` gives them. That descent is the whole of the dialog's rhythm, and it is the
 * pattern in design/50-components.md "The rhythm inside a dialog" rather than a choice made
 * here.
 *
 * **No "(optional)" in the placeholders.** Only Message is required, and it is the one field
 * marked, on its label, by `isRequired`. A placeholder that repeats its own label tells the
 * reader nothing twice and disappears the moment they type (design/50-components.md "Forms").
 *
 * **A real `<form>`**, so Enter submits from any field and the browser's own autofill knows
 * what these boxes are. Validation stays where it was: the caller owns `validators` and
 * `canSubmit`, and the submit button is disabled until the form is valid. Whether disabling is
 * the right pattern at all is an open question in design/50-components.md, and not one to
 * settle while restyling.
 */
export const ContactForm = (props: Props) => {
  const [submitButtonClicked, setSubmitButtonClicked] = useState(false);
  const [interacted, setInteracted] = useState(false);

  const pushGtmEvent = usePushAnonymousGtmEvent();

  useEffect(() => {
    pushGtmEvent({ event: CustomGtmEvent.UA_NON_LEAD_CONTACT_CLICK });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (interacted) {
      pushGtmEvent({ event: CustomGtmEvent.UA_NON_LEAD_CONTACT_FORM_INTERACTED });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interacted]);

  const copy = CONTACT_FORM_COPY[props.variant ?? 'contact'];

  if (props.postStatus === RequestStatus.SUCCESS) {
    return <AfterSubmit copy={copy} />;
  }

  // Each field reports its error only once the reader has left it or tried to submit, which is
  // what the rest of the product does. `validators` answer "is this value acceptable"; an
  // untouched empty Message is not yet a mistake to point at.
  const errorIf = (invalid: boolean, message: string) => (submitButtonClicked && invalid ? message : undefined);
  const change = (patch: Partial<ContactFormData>) => {
    setInteracted(true);
    props.setFields({ ...props.fields, ...patch });
  };

  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        setSubmitButtonClicked(true);
        pushGtmEvent({ event: CustomGtmEvent.UA_NON_LEAD_CONTACT_FORM_SUBMIT });
        props.submit();
      }}
    >
      <Stack gap="2xl">
        <Stack gap="xl">
          <Heading level={2} size="xl" height="xl" weight="semibold" color="gray-900">
            {copy.title}
          </Heading>
          <Paragraph size="md" height="sm" color="gray-600">
            {copy.lede}
          </Paragraph>
        </Stack>

        <Stack gap="xl">
          <Input
            label="Name"
            value={props.fields.name ?? ''}
            onChange={name => {
              change({ name });
            }}
          />
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            value={props.fields.email ?? ''}
            onChange={email => {
              change({ email });
            }}
            error={errorIf(!props.validators.email(props.fields.email), 'Please provide a valid email address.')}
          />
          <Input
            label="Phone"
            type="tel"
            autoComplete="tel"
            value={props.fields.phone ?? ''}
            onChange={phone => {
              change({ phone });
            }}
            error={errorIf(!props.validators.phone(props.fields.phone), 'Please provide a valid phone number.')}
          />
          <Input
            label="Company"
            autoComplete="organization"
            value={props.fields.company ?? ''}
            onChange={company => {
              change({ company });
            }}
          />
        </Stack>

        {/*
         * Message sits in its own block rather than as a fifth row of the group above, because it
         * is the only field the form actually needs and the only one the reader has to compose.
         * The separation is the `2xl` gap the outer `Stack` gives every region of the dialog, which
         * beats the `xl` between the contact rows — more space around a group than within it
         * (design/10-foundations.md "Spacing").
         *
         * The requirement is stated twice, in the two places a reader looks: the `isRequired`
         * asterisk on the label, and a sentence in the lede above. It is *not* repeated as a
         * `description` under the box. Rendered there it reads as a caption on the textarea
         * rather than as a rule about the form, and it sits closer to the error slot than to the
         * label it qualifies.
         */}
        <TextArea
          label="Message"
          isRequired
          rows={5}
          placeholder="What's on your mind?"
          value={props.fields.message ?? ''}
          onChange={message => {
            change({ message });
          }}
          error={errorIf(!props.validators.message(props.fields.message), 'Please type a message.')}
        />

        {/*
         * The action row: right-aligned at the end of the form, and the send failure sits at its
         * left end rather than under the button, so the two do not stack into a second row
         * (design/50-components.md "Where the action row goes"). A whole-request failure is a
         * form-level message; the per-field ones are on their fields.
         */}
        <Box display="flex" alignItems="center" justifyContent="space-between" gap="lg">
          <Text size="sm" height="xs" color="red-500">
            {props.error}
          </Text>
          <Button type="submit" isDisabled={!props.canSubmit} isPending={props.postStatus === RequestStatus.LOADING}>
            {copy.submit}
          </Button>
        </Box>
      </Stack>
    </form>
  );
};

/**
 * The sent state replaces the form in the same card rather than closing it, so the reader sees
 * the send land. `ContactFormModal` clears the fields once the dialog is dismissed.
 */
const AfterSubmit = ({ copy }: { copy: (typeof CONTACT_FORM_COPY)[ContactFormVariant] }) => (
  <Stack gap="lg" align="center" paddingBlock="3xl">
    <SuccessCheckmark color={BouColor.IMPACT_POSITIVE_GREEN} size="4em" />
    <Heading level={2} size="xl" height="xl" weight="semibold" color="gray-900">
      {copy.sentTitle}
    </Heading>
    <Paragraph size="md" height="sm" color="gray-600">
      {copy.sentBody}
    </Paragraph>
  </Stack>
);
